import { ACCEPTED_FILE_TYPES } from "@/api/constants";
import DeviceUtility from "@/helpers/device";

const HEIC_TYPES = ["image/heic", "image/heif"];

// LSP-22361: Chrome on Android opens its Photo Picker when every accept token is image/* or video/*, and that picker
// hands over cloud-only Google Photos items the browser cannot read. Any image/* token also adds a Camera entry and
// any video/* token a Camcorder entry. Chrome's "any file" type alone avoids all three: only the file chooser opens,
// and the Photos app downloads a cloud item before handing it over. validateUploadFile still refuses anything that is
// not an allowed photo, video or (where documents are allowed) PDF.
const ANDROID_ACCEPT = "application/octet-stream";

export function buildAcceptAttribute(options: { allowDocs: boolean }): string {
	if (DeviceUtility.isAndroid) return ANDROID_ACCEPT;

	const isAppleWebKit = DeviceUtility.isSafari || DeviceUtility.isIosOrIpados;
	const types = ACCEPTED_FILE_TYPES.filter((type) => !isAppleWebKit || !HEIC_TYPES.includes(type));

	if (options.allowDocs) {
		types.push("application/pdf");
	}

	return types.join(", ");
}

// Prototype-less ON PURPOSE (archgap round 6). A plain object literal inherits from Object.prototype,
// so `EXTENSION_TO_MIME["constructor"]` returns a FUNCTION rather than undefined — and the same for
// "toString", "valueOf", "hasOwnProperty" and friends. Two consequences, one of them pre-existing:
//   * inferMimeType returns that Function, and getFilesByType calls `.toLowerCase()` on it, which
//     THROWS — a file innocently named `photo.constructor` took down the whole upload batch.
//   * mimeTypeForExtension returns it too; it is truthy and even has a `.length`, so persistedFileExtension
//     read it as positive evidence of a MIME conflict and rewrote the file's extension.
// Object.create(null) removes the inherited names from every lookup site at once; Object.keys still works.
const EXTENSION_TO_MIME: Record<string, string> = Object.assign(Object.create(null) as Record<string, string>, {
	"3gp": "video/3gpp",
	avi: "video/x-msvideo",
	bmp: "image/bmp",
	flv: "video/x-flv",
	gif: "image/gif",
	heic: "image/heic",
	heif: "image/heif",
	jpeg: "image/jpeg",
	jpg: "image/jpeg",
	m4v: "video/x-m4v",
	mkv: "video/x-matroska",
	mov: "video/quicktime",
	mp4: "video/mp4",
	ogv: "video/ogg",
	pdf: "application/pdf",
	png: "image/png",
	svg: "image/svg+xml",
	tif: "image/tiff",
	tiff: "image/tiff",
	webm: "video/webm",
	webp: "image/webp",
	wmv: "video/x-ms-wmv"
});

/**
 * The filename's extension exactly as written, or "" when the name carries none. A name with no dot
 * has NO extension — `"photo".split(".").pop()` returns `"photo"`, which callers then treat as an
 * extension and put in an S3 key (archgap round 5). One dot-handling rule, shared, so that cannot
 * happen in two places differently. Case is preserved: persisted mints must be able to send back the
 * original `JPG` rather than a normalized `jpg`, or they change the key of every pre-existing session.
 */
export function rawExtensionFromFileName(fileName: string): string {
	const dotIndex = fileName.lastIndexOf(".");
	if (dotIndex === -1) return "";
	return fileName.slice(dotIndex + 1);
}

function getFileExtension(fileName: string): string {
	return rawExtensionFromFileName(fileName).toLowerCase();
}

/**
 * The canonical extension for a MIME type, or "" when this table cannot name one. The inverse of
 * {@link mimeTypeForExtension}, over the same table, so the two cannot disagree about what is known.
 *
 * Used to decide whether a better extension than the filename's actually EXISTS. Deriving one from the
 * MIME subtype instead is a guess: `image/pjpeg` would yield the extension `pjpeg`, which is worse than
 * the `JPG` the file already had (archgap round 5).
 */
export function extensionForMimeType(mimeType: string): string {
	const normalized = normalizeMimeType(mimeType);
	if (!normalized) return "";
	// Prefer the subtype when it is itself a real extension for this exact type (image/tiff -> "tiff",
	// image/jpeg -> "jpeg"), so the answer matches what the type plainly reads as. Otherwise fall back to
	// the table's first mapping (image/svg+xml -> "svg"). A type the table cannot name at all returns ""
	// so the caller keeps whatever the filename already had rather than inventing "pjpeg".
	const subtype = normalized.split("/")[1] ?? "";
	if (subtype && EXTENSION_TO_MIME[subtype] === normalized) return subtype;
	return Object.keys(EXTENSION_TO_MIME).find((extension) => EXTENSION_TO_MIME[extension] === normalized) ?? "";
}

/**
 * Whether a filename's extension is plausible enough to put in an S3 key as-is.
 *
 * archgap round 5 established that an extension the table does not list is NOT thereby wrong — `jfif`
 * is a real JPEG extension — so an unlisted one must be preserved rather than rewritten, or every
 * pre-existing session's key changes. But "unlisted" cannot mean "anything at all": rounds 2-4 caught
 * `"jpg "`, `"image/jpeg"`, a 4096-character run, and fullwidth/combining-mark forms all reaching the
 * key derivation. This is the line between the two: a short, plain ASCII alphanumeric token is kept;
 * anything else is replaced by the type's canonical extension.
 */
export function isPlausibleFileExtension(extension: string): boolean {
	return /^[A-Za-z0-9]{1,16}$/.test(extension);
}

/**
 * The MIME type a bare file extension implies, or "" when the extension is unknown. Same table
 * `inferMimeType` uses, exposed for callers that hold an extension rather than a File — e.g.
 * adminUploadTargetInitiator, which must decide whether a persisted session's FILENAME extension
 * still agrees with its recorded fileType before choosing which of the two to trust (LSP-19132).
 */
export function mimeTypeForExtension(extension: string): string {
	// Normalization MUST match getFileExtension exactly — lowercase, and deliberately NO trim.
	// Architecture-gap round 3: an earlier version trimmed, which made this helper disagree with
	// inferMimeType for the same input. A session named `photo.jpg ` then took the "name and type
	// agree" branch in persistedFileExtension and shipped the raw `"jpg "` as the S3 key extension
	// alongside fileType image/jpeg — the mismatched pair this ticket exists to prevent. Without the
	// trim the lookup misses, the branch correctly falls through to the type's subtype, and the pair
	// stays consistent. The lowercase is load-bearing in the other direction: it is what lets `IMG.JPG`
	// still match image/jpeg and keep its original `JPG` extension, so this stays a no-op for every
	// pre-existing session (break-it round 6).
	return EXTENSION_TO_MIME[extension.toLowerCase()] ?? "";
}

/**
 * Returns the file's MIME type, falling back to extension-based inference
 * when the browser doesn't populate file.type (common for .wmv, .avi, .mov on drag-and-drop).
 */
export function inferMimeType(file: File): string {
	if (file.type) return file.type;
	return EXTENSION_TO_MIME[getFileExtension(file.name)] ?? "";
}

/**
 * Returns a File with `type` forced to `mimeType`, regardless of what the file currently declares.
 * Unlike {@link ensureFileType} (which only fills an EMPTY type), this OVERRIDES a wrong declared
 * type — used when a stronger signal (e.g. a magic-byte read) disagrees with the OS-reported type
 * (LSP-17928 break-it round 4).
 */
export function withCorrectedFileType(file: File, mimeType: string): File {
	return new File([file], file.name, { type: mimeType, lastModified: file.lastModified });
}

/**
 * Returns a File with the correct MIME type set. If the browser left file.type empty,
 * a new File is created with the inferred type so that S3 presigned URL signatures match.
 */
export function ensureFileType(file: File): File {
	if (file.type) return file;
	const mimeType = inferMimeType(file);
	if (!mimeType) return file;
	return withCorrectedFileType(file, mimeType);
}

/**
 * RFC 2045 §5.1: a MIME value may carry parameters (`image/jpeg; charset=UTF-8`) and its type/subtype
 * tokens are case-insensitive. Reduce to the bare, lowercase essence.
 */
export function normalizeMimeType(value: string | null | undefined): string {
	return (value ?? "").split(";")[0].trim().toLowerCase();
}

// A detected value is usable ONLY if it is a well-formed `type/subtype`. The file-type library always
// returns one, so this is defence in depth — but trusting a malformed value fails silently and badly,
// because the upload initiators derive the S3 key extension from `file.type.split("/")[1]`: `image/`
// and `image//jpeg` yield an empty extension, an embedded NUL yields an unusable File.type, and a
// pathological subtype yields a multi-kilobyte object key. Token grammar and the 127-per-part bound
// are RFC 6838 §4.2; MAX_MIME_LENGTH short-circuits a huge input before the pattern matcher sees it.
const WELL_FORMED_MIME = /^[a-z0-9][a-z0-9!#$&^_.+-]{0,126}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/;
const MAX_MIME_LENGTH = 255;

export function isUsableDetectedMimeType(detected: string): boolean {
	return detected.length > 0 && detected.length <= MAX_MIME_LENGTH && WELL_FORMED_MIME.test(detected);
}

export type MimeCorrectionAction = "unchanged" | "canonicalized" | "corrected" | "no-usable-signal";

export interface MimeCorrection {
	file: File;
	action: MimeCorrectionAction;
	declaredType: string;
	detectedMimeType: string | null;
	appliedType: string;
}

/**
 * LSP-19132 — THE single implementation of "which MIME type does this file actually carry".
 *
 * The browser derives `file.type` from the filename extension, so a JPEG named `.heic` (iCloud for
 * Windows converts the bytes but keeps the `.HEIC` name) is declared `image/heic`. That declared type
 * reaches the API, which mints an S3 key ending `.heic`; the snapshot Lambda branches on the KEY's
 * extension, runs heif-convert against JPEG bytes, throws, and never reaches sendFinalizeMessage —
 * the only producer of the event that completes a Snapshot row. The upload reports success at every
 * layer while the photo silently never appears.
 *
 * This lives here, exported, because it previously existed as TWO copies — one in useFileUploadFlow
 * and one in ActionUploadPhoto — which drifted apart. Four architecture-gap/break-it rounds each found
 * a normalization defect in one copy that the other did not have. Callers must not re-implement it.
 *
 * Note the `canonicalized` case: when declared and detected AGREE but the declared value is not
 * already canonical (`image/jpeg;charset=UTF-8` vs `image/jpeg`), the file is still rewritten. Leaving
 * it produced `fileExtension: "jpeg;charset=utf-8"` from `file.type.split("/")[1]` — agreement on the
 * normalized values is not the same as the raw value being safe to ship (archgap round 4).
 *
 * Identity is preserved whenever nothing needs changing, because callers and tests compare instances.
 */
export function correctDeclaredMimeType(file: File, detectedMimeType: string | null): MimeCorrection {
	const detected = normalizeMimeType(detectedMimeType);
	const declared = normalizeMimeType(file.type);
	const declaredNeedsCanonicalizing = declared.length > 0 && declared !== file.type;
	const base = { declaredType: file.type, detectedMimeType };

	if (!isUsableDetectedMimeType(detected)) {
		// Either an inconclusive read (a cloud-hosted file whose slice read timed out, so the validator
		// fell back to the OS/extension MIME and reported null) or a value we cannot trust as a MIME
		// type. Either way there is no honest signal to correct FROM — but a non-canonical DECLARED
		// value must still not be shipped raw into the key derivation.
		if (declaredNeedsCanonicalizing) {
			return { ...base, file: withCorrectedFileType(file, declared), action: "canonicalized", appliedType: declared };
		}
		return { ...base, file, action: "no-usable-signal", appliedType: file.type };
	}
	if (detected === declared) {
		if (declaredNeedsCanonicalizing) {
			return { ...base, file: withCorrectedFileType(file, declared), action: "canonicalized", appliedType: declared };
		}
		return { ...base, file, action: "unchanged", appliedType: file.type };
	}
	// The NORMALIZED detected value is what gets written — never the raw string.
	return { ...base, file: withCorrectedFileType(file, detected), action: "corrected", appliedType: detected };
}

/**
 * LSP-19418 — adds a newly-picked batch to the batch already staged for upload.
 *
 * The staged upload page used to REPLACE its batch on every selection, which is why "select more
 * media to add" was impossible: dropping a third file onto two staged ones left one file staged.
 * Accumulating instead needs a re-pick guard, because the OS picker does not remember the previous
 * selection — the natural way to add one photo on a phone is to re-pick the ones already chosen
 * plus the new one, and without this that would upload the earlier files twice.
 *
 * Identity is name + size + lastModified together. Two phones both produce `IMG_0001.jpg`, so the
 * name alone is not an identity; `correctDeclaredMimeType` may hand back a NEW File with a rewritten
 * `type`, so instance identity and `type` are both unusable. These three survive that rewrite
 * (`withCorrectedFileType` preserves all of them) and are the only File fields a browser guarantees.
 *
 * Order is "staged first, then the new arrivals in the order the user picked them".
 *
 * `skipped` carries the files it dropped, so the caller's breadcrumb can name them. A skip is
 * deliberately silent for the user (the file IS staged, just already), but "I picked it and the
 * count never moved" still has to be diagnosable — CLAUDE.md "Diagnostic logging when fixing
 * silent failures". Break-it round 2: the caller used to log every incoming name instead, which
 * told that triage the wrong files were dropped.
 */
export function mergeStagedFiles(stagedFiles: File[], incomingFiles: File[]): { files: File[]; skipped: File[] } {
	const identify = (file: File) => `${file.name}\u0000${file.size}\u0000${file.lastModified}`;
	const alreadyStaged = new Set(stagedFiles.map(identify));
	const files = [...stagedFiles];
	const skipped: File[] = [];
	for (const file of incomingFiles) {
		const identity = identify(file);
		if (alreadyStaged.has(identity)) {
			skipped.push(file);
			continue;
		}
		alreadyStaged.add(identity);
		files.push(file);
	}
	return { files, skipped };
}

/**
 * This function separates the files into images, documents and videos.
 * @param {File[]} files - The files to separate.
 * @returns {images: File[], documents: File[], videos: File[]} - An object with the images, documents and videos.
 */
export function getFilesByType(files: File[]) {
	const images: File[] = [];
	const documents: File[] = [];
	const videos: File[] = [];
	for (const file of files) {
		// LSP-17928 break-it round (AI PR Review): must lowercase before the startsWith checks — an
		// uppercase declared type (e.g. "IMAGE/JPEG") is exactly the case gate 1's matchesAllowed was
		// made case-insensitive for, but this bucketing pass compared raw case and dropped it into no
		// bucket at all, silently vanishing a real photo/video from a checklist upload.
		const mimeType = inferMimeType(file).toLowerCase();
		if (mimeType.startsWith("image/")) {
			images.push(file);
		} else if (mimeType.startsWith("application/pdf")) {
			documents.push(file);
		} else if (mimeType.startsWith("video/")) {
			videos.push(file);
		}
	}
	return { images, documents, videos }; //make sure they are in this order
}
